import { ApiError } from '@/app/lib/apiError'
import { quantErrorMessage } from '@/app/lib/quant/quantErrors'
import { paramsForConfig } from '@/app/lib/quant/paramsForConfig'
import type { ParamDef, ParamValue, SavedConfig } from '@/app/lib/quant/types'

/** Spec 073 B3: the saved-config helpers (pure). */

/** Codes the saved-config routes answer with; `detail` is user-facing for each. */
const CONFIG_CODES: ReadonlySet<string> = new Set(['config_limit', 'invalid_config', 'invalid_config_name'])

/** One sentence for a failed saved-config request: the server's detail for its own codes, else the shared quant copy. */
export function savedConfigErrorMessage(error: unknown): string {
  if (error instanceof ApiError && CONFIG_CODES.has(error.code ?? '')) return error.message
  return quantErrorMessage(error)
}

/** The saved config with this exact name, if any (names are case-sensitive, like the server's unique key). */
export function findSavedConfig(configs: SavedConfig[], name: string): SavedConfig | undefined {
  return configs.find((c) => c.name === name)
}

/**
 * Raw form values for a saved param map: known names only (unknown ones are reported in `dropped`),
 * every other field at its default, bools as booleans and everything else as the text a control holds.
 */
export function rawFromSaved(
  saved: Record<string, ParamValue | null>,
  defs: ParamDef[],
): { raw: Record<string, ParamValue>; dropped: string[] } {
  const present: Record<string, ParamValue> = {}
  for (const [name, value] of Object.entries(saved)) if (value !== null) present[name] = value
  const { kept, dropped } = paramsForConfig(saved as Record<string, ParamValue>, defs)
  const raw: Record<string, ParamValue> = {}
  for (const def of defs) {
    const value = def.name in kept && def.name in present ? present[def.name] : def.default
    raw[def.name] = def.kind === 'bool' ? value === true : value === null || value === undefined ? '' : String(value)
  }
  return { raw, dropped }
}
